import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import SearchInput from '../components/common/SearchInput';
import Pagination from '../components/common/Pagination';
import BySongsTable from '../components/BySongsTable';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';
import SectionTabs from '../components/SectionTabs';
import { Users, Disc, Music, Globe, User, Library, ListMusic, Package, Boxes } from 'lucide-react';
import usePhysicalExists from '../hooks/usePhysicalExists';

function ByMonthSongsPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const isAdmin = me?.adminYn === 'Y';
  const [loading, setLoading] = useState(false);
  const [songs, setSongs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [month, setMonth] = useState('');
  const [monthList, setMonthList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 15;

  // 월 리스트 가져오기
  useEffect(() => {
    const fetchMonths = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/ByClient/getMonthList`
        );
        const months = res.data || [];
        setMonthList(months);

        if (!month && months.length > 0) {
          setMonth(months[0]);
        }
      } catch (err) {
        console.error('월 리스트 조회 오류:', err);
      }
    };
    fetchMonths();
  }, [month]);

  // 해당 월의 곡 리스트 가져오기
  useEffect(() => {
    if (!me || !month) return;
    const clientId = me.id;
    const adminYn = me.adminYn === 'Y' ? 'Y' : 'N';

    const fetchSongs = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/BySong/byMonthSongs`,
          {
            params: {
              search: searchKeyword,
              page: currentPage,
              limit: pageSize,
              month: month,
              clientId,
              adminYn,
            },
          }
        );

        const items = response.data.items;
        setSongs(items);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalCount);
      } catch (error) {
        console.error('서버 접속 에러:', error);
      } finally {
        setLoading(false);
      }
    };

    if (month) {
      fetchSongs();
    }
  }, [me, searchKeyword, currentPage, month]);

  // 페이지 이동
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // 월 이동
  const currentIndex = monthList.indexOf(month);
  const handlePrevMonth = () => {
    if (currentIndex < monthList.length - 1) {
      setMonth(monthList[currentIndex + 1]);
      setCurrentPage(1);
    }
  };
  const handleNextMonth = () => {
    if (currentIndex > 0) {
      setMonth(monthList[currentIndex - 1]);
      setCurrentPage(1);
    }
  };

  // 월 포맷팅
  const formatMonth = (monthStr) => {
    const [year, m] = monthStr.split('-');
    return `${year}년 ${parseInt(m, 10)}월 곡별 정산`;
  };

  const isPrevDisabled =
    currentIndex === -1 || currentIndex === monthList.length - 1;
  const isNextDisabled = currentIndex === -1 || currentIndex === 0;

  const hasPhysical = usePhysicalExists();
  const reportTabs = [
    { label: isAdmin ? '당월권리사별' : '월별리포트', to: '/ByClientLatestMonth', icon: Users },
    { label: '당월앨범별', to: '/ByMonthAlbums', icon: Disc },
    { label: '당월곡별', to: '/ByMonthSongs', icon: Music },
    { label: '당월플랫폼별', to: '/ByMonthPlatforms', icon: Globe },
    { label: '당월아티스트별', to: '/ByMonthArtists', icon: User },
    ...(hasPhysical ? [{ label: '당월음반별', to: '/ByPhysicalMonth', icon: Package }] : []),
    { label: '앨범별누적', to: '/ByAlbums', icon: Library },
    { label: '곡별누적', to: '/BySongs', icon: ListMusic },
    ...(hasPhysical ? [{ label: '음반누적', to: '/ByPhysicalTotal', icon: Boxes }] : []),
  ];

  return (
    <>
        {/* 월 이동 영역 */}
        <div className="flex mb-8 justify-center items-center gap-5">
          <button
            onClick={handlePrevMonth}
            disabled={isPrevDisabled}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            {month ? formatMonth(month) : ''}
          </h1>
          <button
            onClick={handleNextMonth}
            disabled={isNextDisabled}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
        <SectionTabs items={reportTabs} />

        {/* 검색창 */}
        <SearchInput
          placeholder="곡명 또는 앨범명 또는 아티스트명 검색"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={() => {
            setCurrentPage(1); // 검색 시 페이지 초기화
            setSearchKeyword(searchTerm); // 검색 실행
          }}
        />

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3"></div>
            <div className="text-sm text-slate-500">데이터 불러오는 중...</div>
          </div>
        ) : (
          <>
            {/* 테이블 */}
            <BySongsTable
              data={songs}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              me={me}
              month={month}
            />

            {/* 페이지네이션 */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
    </>
  );
}

export default ByMonthSongsPage;
